/**
 * NotificationInbox: the learner's in-app inbox (plan docs/plans/lms-plan.md
 * 9.2, 9.4; R4, R8; WP-41). Home shows it as its right-hand column (7.1);
 * WP-48's bell may render the same component behind the AppBar button.
 *
 * - GET /me/notifications lists the caller's rows in this network, unread
 *   first, then newest first (WP-33); "Show more" pages on. The unread pill
 *   shows GET /me/notifications/unread-count (every unread row, not only the
 *   loaded page), falling back to the loaded rows when that read fails.
 * - Opening a row marks it read (POST /me/notifications/:id/read) and then
 *   follows its url, a path inside the module (9.2: /lms/records,
 *   /lms/courses/<id>, ...). The row turns read at once; a failed mark puts it
 *   back and says so (toasts for every action's outcome, 9.4).
 * - "Mark all read" posts read-all (this network only, 18.21).
 *
 * Nothing here polls: the 60-second summary poll is LmsProvider's (D14,
 * WP-48). `reloadKey` reloads the list when the host asks (Home's refresh).
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { apiErrorMessage } from '../api/client';
import { getUnreadCount, listMyNotifications, markAllNotificationsRead, markNotificationRead } from '../api/notifications';
import type { LmsNotification } from '../api/notifications';
import { outlineBtnCls, touchTarget } from '../../ui/styles';
import { useToast } from '../../ui/Toast';

export interface NotificationInboxProps {
  /** Rows per page (Appendix A: at most 200; default 10). */
  pageSize?: number;
  /** Changing it reloads the first page. */
  reloadKey?: number;
  /** Told the unread count whenever it changes (a host badge). */
  onUnreadChange?: (unread: number) => void;
  className?: string;
}

/** "3 hours ago", or '' for a missing or unreadable instant. */
function ago(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : formatDistanceToNow(d, { addSuffix: true });
}

/** Only a path inside the module is followed (9.2: url is a path inside the module). */
function moduleTarget(url: string | null | undefined): string | null {
  return typeof url === 'string' && /^\/lms(\/|$)/.test(url) ? url : null;
}

export function NotificationInbox({ pageSize = 10, reloadKey = 0, onUnreadChange, className = '' }: NotificationInboxProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState<LmsNotification[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  // Unique per instance: Home and the bell (WP-48) may both render an inbox.
  const headingId = useId();
  // The caller's unread total in the network (GET unread-count); the loaded rows count when it is unknown.
  const [unreadTotal, setUnreadTotal] = useState<number | null>(null);
  const unread = unreadTotal ?? items.filter((n) => !n.read_at).length;

  useEffect(() => {
    onUnreadChange?.(unread);
  }, [unread, onUnreadChange]);

  /** Load the first page; `stale()` says the answer belongs to an earlier load (a reload or an unmount). */
  const load = useCallback(
    async (stale: () => boolean = () => false) => {
      setLoading(true);
      setError(null);
      try {
        const [page, total] = await Promise.all([
          listMyNotifications({ limit: pageSize, offset: 0 }),
          getUnreadCount().catch(() => null),
        ]);
        if (stale()) return;
        setItems(Array.isArray(page?.items) ? page.items : []);
        setUnreadTotal(total);
        setHasMore(page?.page?.hasMore === true);
      } catch (err) {
        if (stale()) return;
        setError(apiErrorMessage(err, 'Your notifications could not be loaded.'));
      } finally {
        if (!stale()) setLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load, reloadKey]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listMyNotifications({ limit: pageSize, offset: items.length });
      const seen = new Set(items.map((n) => n.id));
      setItems((prev) => prev.concat((page?.items ?? []).filter((n) => !seen.has(n.id))));
      setHasMore(page?.page?.hasMore === true);
    } catch (err) {
      toast(apiErrorMessage(err, 'More notifications could not be loaded.'), { type: 'error' });
    } finally {
      setLoadingMore(false);
    }
  };

  const open = async (row: LmsNotification) => {
    const target = moduleTarget(row.url);
    if (!row.read_at) {
      const readAt = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.id === row.id ? { ...n, read_at: readAt } : n)));
      setUnreadTotal((t) => (t === null ? null : Math.max(0, t - 1)));
      try {
        const saved = await markNotificationRead(row.id);
        if (saved && saved.id === row.id) {
          setItems((prev) => prev.map((n) => (n.id === row.id ? { ...n, read_at: saved.read_at ?? readAt } : n)));
        }
      } catch (err) {
        setItems((prev) => prev.map((n) => (n.id === row.id ? { ...n, read_at: null } : n)));
        setUnreadTotal((t) => (t === null ? null : t + 1));
        toast(apiErrorMessage(err, 'The notification could not be marked as read.'), { type: 'error' });
        return;
      }
    }
    if (target) navigate(target);
  };

  const markAll = async () => {
    if (markingAll || unread === 0) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsRead();
      const readAt = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: readAt })));
      setUnreadTotal(0);
      toast('All notifications marked as read.', { type: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'The notifications could not be marked as read.'), { type: 'error' });
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <section
      className={`rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 ${className}`}
      aria-labelledby={headingId}
      data-testid="lms-inbox"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h2 id={headingId} className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Bell className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Inbox
          {unread > 0 ? (
            <span
              className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
              data-testid="lms-inbox-unread"
            >
              {`${unread} unread`}
            </span>
          ) : null}
        </h2>
        <button
          type="button"
          className={outlineBtnCls()}
          onClick={() => void markAll()}
          disabled={markingAll || unread === 0}
          data-testid="lms-inbox-read-all"
        >
          <CheckCheck className="h-4 w-4" aria-hidden="true" />
          Mark all read
        </button>
      </div>

      {loading ? (
        <div className="space-y-2 p-4" aria-busy="true" data-testid="lms-inbox-loading">
          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ) : error ? (
        <div className="space-y-2 p-4 text-sm text-red-700 dark:text-red-400" data-testid="lms-inbox-error">
          <p>{error}</p>
          <button type="button" className={outlineBtnCls()} onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-4 py-8 text-center" data-testid="lms-inbox-empty">
          <Inbox className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Nothing new</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">New assignments, reminders and certificates appear here.</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-inbox-list">
          {items.map((n) => {
            const isUnread = !n.read_at;
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => void open(n)}
                  className={`${touchTarget} flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/50`}
                  data-testid="lms-inbox-item"
                  data-id={n.id}
                  data-unread={isUnread ? 'true' : 'false'}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${isUnread ? 'bg-primary-600 dark:bg-primary-400' : 'bg-transparent'}`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm ${isUnread ? 'font-semibold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
                      {n.title}
                      {isUnread ? <span className="sr-only"> (unread)</span> : null}
                    </span>
                    {n.body ? (
                      <span className="mt-0.5 line-clamp-2 block text-xs text-gray-600 dark:text-gray-400">{n.body}</span>
                    ) : null}
                    {n.created_at ? (
                      <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400">{ago(n.created_at)}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && !error && hasMore ? (
        <div className="border-t border-gray-200 px-4 py-2 dark:border-gray-700">
          <button
            type="button"
            className={outlineBtnCls()}
            onClick={() => void loadMore()}
            disabled={loadingMore}
            data-testid="lms-inbox-more"
          >
            {loadingMore ? 'Loading...' : 'Show more'}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default NotificationInbox;
